import React from 'react';
import { ArrowRight, MapPin, Star, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { heroImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="hero-tropical relative min-h-screen flex items-center" aria-label="Property Management Services in Puerto Plata and Las Terrenas">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={heroImages.beach}
          alt="Beautiful tropical beach in Puerto Plata, Dominican Republic - vacation rental property management location"
          className="w-full h-full object-cover" />

      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 animate-fade-in-up">
            <MapPin className="w-4 h-4 text-[#00FFFF]" />
            <span className="text-white text-sm font-medium">{t('hero.badge')}</span>
          </div>

          {/* Main heading */}
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {t('hero.title1')}
            <span className="block text-[#FFFF00] mt-2">— {t('hero.title2')}</span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {t('hero.subtitle')} <span className="text-[#FFFF00] font-semibold">{t('hero.peaceOfMind')}</span> {t('hero.subtitleEnd')}
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-4 mb-10 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center backdrop-blur-sm border border-[#000080]/50 gap-2 px-4 py-2 rounded-lg bg-[#000080]/80">
              <Star className="w-5 h-5 text-[#FFFF00]" />
              <span className="text-white text-sm font-medium">{t('hero.superHost')}</span>
            </div>
            <div className="flex items-center backdrop-blur-sm border border-[#000080]/50 gap-2 px-4 py-2 rounded-lg bg-[#000080]/80">
              <Shield className="w-5 h-5 text-[#FFFF00]" />
              <span className="text-white text-sm font-medium">{t('hero.noGringo')}</span>
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Button
              onClick={() => scrollToSection('#packages')}
              size="lg"
              className="btn-tropical bg-[#FFFF00] hover:bg-[#FFD700] text-[#000080] font-bold text-lg px-8 py-6 shadow-lg">

              {t('hero.viewPackages')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => scrollToSection('#contact')}
              size="lg"
              variant="outline"
              className="border-2 border-[#FFFF00] text-[#FFFF00] hover:bg-[#FFFF00] hover:text-[#000080] font-semibold text-lg px-8 py-6 bg-transparent">

              {t('hero.contactUs')}
            </Button>
          </div>

          {/* Price highlight */}
          <div className="mt-10 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <p className="text-white/80 text-lg">
              {t('hero.packagesStart')}{' '}
              <span className="text-[#FFFF00] font-bold text-2xl">15% per month.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Decorative flowers */}
      <div className="absolute bottom-0 right-0 w-64 h-64 opacity-30 hidden lg:block">
        <img
          src={heroImages.bougainvillea}
          alt="Bougainvillea"
          className="w-full h-full object-cover rounded-tl-full" />

      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-2 h-3 bg-white/70 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>);

};

export default HeroSection;